import _ from "lodash";
import DataProps from "./DataProps";
import { setColorCode } from "./ColorCode";
import { circularCharts } from "../../config/circular.chart";
import {
  setLegend,
  setYAxisScaleAndTitles,
  setXAxisScaleAndTitles,
  setDataLabel,
  prepareMultiLineLabelData,
  getStackedDataSum,
  stackedDataSumObj,
  setLayoutforOutlabels,
  checkUniDataLineChart,
  prepareUniDataLineChart,
} from "./chart.options";
import { LINE } from "../../config/chart.type";
import { EI_OI_CHART_TITLES, EISM_DOUGHNUT_TITLE,PINK_FOR_GRAPH,
  BLUE_FOR_GRAPH, EISM_TITLE } from '../../lib/constants';

const toNumericArray = (values) =>
  ([]).concat(values ?? []).map((v) => parseFloat(v) || 0);

const getFirstArrayValue = (source) => {
  if (!source || typeof source !== 'object') {
    return [];
  }
  return Object.values(source).find(Array.isArray) || [];
};

const getStyleByKey = (styles, key) =>
  styles.find((item) => item.key === key) || {};

const getStyleLabel = (style, fallback) =>
  style?.title || style?.key || fallback;

const sortKeysAsc = (keys = []) =>
  (keys.some((key) => String(key ?? '').includes('-'))
    ? [...keys].sort((a, b) =>
        String(a ?? '').localeCompare(String(b ?? ''), undefined, {
          sensitivity: 'base',
        }),
      )
    : [...keys]);

const buildEismPointStyles = (flags, series, baseColor) => {
  const pointBackgroundColor = [];
  const pointBorderColor = [];
  const pointRadius = [];
  const pointHoverBackgroundColor = [];
  const pointHoverBorderColor = [];
  const max = Math.min(series.length, flags.length);
  for (let i = 0; i < max; i++) {
    const active = flags[i] === true || flags[i] === 'true';
    const color = active ? PINK_FOR_GRAPH : baseColor;
    pointBackgroundColor.push(color);
    pointBorderColor.push(color);
    pointRadius.push(active ? 6 : 3);
    pointHoverBackgroundColor.push(color);
    pointHoverBorderColor.push(color);
  }
  return {
    pointBackgroundColor,
    pointBorderColor,
    pointRadius,
    pointHoverBackgroundColor,
    pointHoverBorderColor,
  };
};

const applyEismHighlights = (datasets, chartStyles, chartDataJson) => {
  const impactStyle = getStyleByKey(chartStyles, 'coImpact');
  const impactLabel = getStyleLabel(impactStyle, 'coImpact');
  const coImpactDataset = datasets.find(
    (dataset) => dataset.label === impactLabel,
  );
  if (!coImpactDataset) {
    return false;
  }
  const flags = [].concat(chartDataJson?.ffr ?? []);
  const series = [].concat(chartDataJson?.coImpact ?? []);
  const lineColor =
    impactStyle.color || coImpactDataset.borderColor || BLUE_FOR_GRAPH;
  const pointStyles = buildEismPointStyles(flags, series, lineColor);
  coImpactDataset.borderColor = lineColor;
  Object.assign(coImpactDataset, pointStyles);
  return true;
};

const buildNonCircularChart = ({
  chartDataJson,
  type,
  details,
  labels,
  label,
  chartData,
  columns,
}) => {
  const { styles, options } = details;
  const formatter = options?.formatter;
  const chartStyles = styles?.length
      ? styles
      : prepareDynamicChartStyles(columns, type, options.hideXaxisTitle);
  const safeLabels = Array.isArray(labels) ? labels : [];
  const isUniDataLineChart =
    type === 'line' && checkUniDataLineChart(chartDataJson);
  const chartLabels = isUniDataLineChart
    ? ['', ...safeLabels, '']
    : safeLabels;
  const xAxisStyle = chartStyles.find((item) => item.key === label);
  const xAxisTitle = xAxisStyle?.title || xAxisStyle?.key || '';
  const rawNonCircularChartData = isUniDataLineChart
    ? prepareUniDataLineChart(chartDataJson)
    : chartDataJson;
  const { datasets, yAxisTitles } = prepareNonCircularChartData(
    rawNonCircularChartData,
    type,
    chartStyles,
    formatter,
    details.errorGraph,
  );

  let eismPrimaryLabel = null;
  if (details.title === EISM_TITLE) {
    const primaryStyle = getStyleByKey(chartStyles, 'primaryInterval');
    eismPrimaryLabel = getStyleLabel(primaryStyle, 'primaryInterval');
    applyEismHighlights(datasets, chartStyles, chartDataJson);
  }

  if (EI_OI_CHART_TITLES.includes(details.title)) {
    datasets.forEach((obj) => {
      if (obj.type === 'bar') {
        obj.maxBarThickness = 40;
      }
    });
  }
  const stackedDataSum = getStackedDataSum(datasets);
  options.showDatalabelsSum && datasets.push(stackedDataSumObj(stackedDataSum));
  chartData.data = {
    labels: chartLabels,
    datasets,
  };
  const chartOptions = setLegend(options.legend);
  if (details.title === EISM_TITLE) {
    chartOptions.tooltips = chartOptions.tooltips || {};
    const existingFilter =
      typeof chartOptions.tooltips.filter === 'function'
        ? chartOptions.tooltips.filter
        : () => true;
    chartOptions.tooltips.filter = (tooltipItem, data) => {
      const tooltipLabel = data?.datasets?.[tooltipItem?.datasetIndex]?.label;
      const keep = tooltipLabel !== eismPrimaryLabel;
      return existingFilter(tooltipItem, data) && keep;
    };
  }
  chartOptions.tooltips = chartOptions.tooltips || {};
  chartOptions.tooltips.callbacks = chartOptions.tooltips.callbacks || {};
  chartOptions.tooltips.callbacks.title = (tooltipItems, data) => {
    const tooltipItem = Array.isArray(tooltipItems)
      ? tooltipItems[0]
      : tooltipItems;
    return data?.labels?.[tooltipItem?.index] ?? '';
  };
  chartOptions.plugins = {
    datalabels: setDataLabel(options.showDataLabel, stackedDataSum).datalabels,
  };
  chartOptions.scales = buildChartScales(
    chartLabels,
    chartData,
    xAxisTitle,
    yAxisTitles,
    rawNonCircularChartData,
    options,
    details.errorGraph,
  );
  chartData.chartOptions = chartOptions;
};

const buildCircularChart = ({
  chartDataJson,
  type,
  details,
  labels,
  chartData,
}) => {
  const { styles, options } = details;
  const datasets = prepareCircularChartData(chartDataJson, type, styles, labels);
  chartData.data = {
    labels,
    datasets,
  };
  const numericalDataArray = datasets[0].data.map((data) => {
    return parseFloat(data) || 0;
  });
  const chartOptions = setLegend(options.legend);
  const dataLabelData = setDataLabel(options.showDataLabel, numericalDataArray);
  chartOptions.layout = setLayoutforOutlabels();
  chartOptions.plugins = {
    datalabels: dataLabelData.datalabelsCircular,
    outlabels: dataLabelData.outlabelsCircular,
  };

  // Apply custom tooltip only for the specified chart title
  if (details.title.includes(EISM_DOUGHNUT_TITLE)) {
    chartOptions.tooltips = {
      callbacks: {
        label: function(tooltipItem, data) {
          const dataset = data.datasets[tooltipItem.datasetIndex];
          const label = data.labels[tooltipItem.index];
          const co2Val = dataset.co2?.[tooltipItem.index];
          const lines = [`${label}: ${co2Val}`].filter(Boolean);
          return lines;
        },
      },
    };
  }
  chartData.chartOptions = chartOptions;
};

const buildChartScales = (
  labels,
  chartData,
  xAxisTitle,
  yAxisTitles,
  rawNonCircularChartData,
  options,
  isErrorGraph,
) => {
  const labelsData = options.multiLineLabel
    ? prepareMultiLineLabelData(labels, chartData)
    : labels;
  const scales = {
    xAxes: setXAxisScaleAndTitles(
      xAxisTitle,
      labelsData,
      options.stacked,
      options.showGridLines,
    ),
  };
  if (!_.isEmpty(yAxisTitles)) {
    const yMaxObj = getYMax(rawNonCircularChartData, isErrorGraph);
    scales.yAxes = setYAxisScaleAndTitles(yAxisTitles, options.yAxes, yMaxObj);
  }
  return scales;
};

export const prepareCircularChartData = (chartDataJson, chartType, styles, labels) => {
  const { backgroundColors } = setBackGroundColorAndType(
    styles,
    labels,
    chartType
  );
  // Expect shape: { value: [...], co2: [...], total: [...] }
  const values = toNumericArray(
    chartDataJson?.value ?? getFirstArrayValue(chartDataJson),
  );
  const co2 = toNumericArray(chartDataJson?.co2);
  const total = toNumericArray(chartDataJson?.total);

  return [
    {
      data: values,
      co2,
      total,
      backgroundColor: backgroundColors,
      hoverBackgroundColor: backgroundColors,
    },
  ];
};
const percentFormatter = (value) => `${parseFloat(value).toFixed(2)}%`;
const setFormatterDataLabels = (formatter, formatterTitle) => {
  let formatterValue = () => {};
  if (formatterTitle === "OI-Rates" || formatterTitle === "EI-Rates") {
    formatterValue = percentFormatter;
  }
  return {...formatter?.formatterOption, formatter: formatterValue }
};
/**
 *
 * @param {*} styles
 * @param {*} labels
 * @param {*} isCircle
 */
const setBackGroundColorAndType = (styles, labels, chartType) => {
  const isCircle = checkIfCircularChart(chartType);
  const backgroundColors = new Array(labels.length);
  const chartTypes = new Array(labels.length);
  const yAxisTitles = new Array(labels.length);
  const yAxisIDs = new Array(labels.length);
  const pointStyles = new Array(labels.length);
  const pointRadi = new Array(labels.length);
  const borderWidths = new Array(labels.length);
  styles.forEach((item) => {
    const { value, key, type, title, yAxisID, color, pointStyle, pointRadius, borderWidth } = item;
    const lbl = isCircle ? value : key;
    if (labels.includes(lbl)) {
      backgroundColors[labels.indexOf(lbl)] = color;
      chartTypes[labels.indexOf(lbl)] = type || chartType;
      yAxisTitles[labels.indexOf(lbl)] = title || "";
      yAxisIDs[labels.indexOf(lbl)] = yAxisID || "";
      pointStyles[labels.indexOf(lbl)] = pointStyle ?? null;
      pointRadi[labels.indexOf(lbl)] = pointRadius ?? null;
      borderWidths[labels.indexOf(lbl)] = borderWidth ?? null;
    }
  });
  backgroundColors.includes(undefined) && setColorCode(backgroundColors);
  return {
    backgroundColors,
    chartTypes,
    yAxisTitles,
    yAxisIDs,
    pointStyles,
    pointRadi,
    borderWidths,
  };
};
/**
 *
 * @param {*} chartDataJson
 * @param {*} chartType
 * @param {*} isCircle
 * @param {*} styles
 */


const prepareNonCircularChartData = (chartDataJson, chartType, styles, formatter, errorGraph = false) => {
  const titles = sortKeysAsc(Object.keys(chartDataJson));
  const { backgroundColors, chartTypes, yAxisTitles, yAxisIDs, pointStyles, pointRadi, borderWidths } =
    setBackGroundColorAndType(styles, titles, chartType);
  return {
    datasets: titles.map((title, index) => {
      const dataProps =
        DataProps[chartType === "mix" ? chartTypes[index] : chartType];

        if (chartTypes[index] === LINE) {
          const style = _.find(styles, (item) => item.key === titles[index]);
          dataProps.pointRadius =
            style.pointRadius !== undefined
              ? style.pointRadius
              : dataProps.pointRadius;
        }
        let styleAttributes = {
        ...dataProps,
        label: yAxisTitles[index],
        backgroundColor: backgroundColors[index],
        borderColor: backgroundColors[index],
        pointBackgroundColor: backgroundColors[index],
        pointBorderColor: backgroundColors[index],
        pointHoverBackgroundColor: backgroundColors[index],
        pointHoverBorderColor: backgroundColors[index],
        hoverBorderColor: backgroundColors[index],
        maxBarThickness: errorGraph ? 3 : 80,
        data: chartDataJson[title],
        type: chartTypes[index],
        yAxisID: yAxisIDs[index],
      }
      if (formatter?.formatterOption?.display && formatter?.formatterChart?.includes(chartTypes[index])) {
          styleAttributes["datalabels"] = setFormatterDataLabels(formatter, formatter?.formatterTitle);
      }
      if (errorGraph) {
        styleAttributes = {
          ...styleAttributes,
          pointStyle: pointStyles[index],
          pointRadius: pointRadi[index],
          // pointHoverRadius: pointHoverRadi[index] ?? styleAttributes.pointHoverRadius,s
          borderWidth:borderWidths[index],
        };
      }
      return { ...styleAttributes };
    }),
    yAxisTitles,
  };
};

export const checkIfCircularChart = (chartName) => {
  return circularCharts.includes(chartName);
};

export const prepareDynamicChartStyles = (columns, type, hideXaxisTitle) => {
  //  In non circular charts, when 2 types of xaxis values are present,
  //  1, one for for overall xaxis (ie) x axis title
  //  2, one for individual items
  //  hideXaxisTitle = optional, boolean = hides x axis title

  const chartLegends = columns.reduce((acc, column) => {
    if (type !== "mix") {
      acc.push({
        key: column,
        title: column,
      });
    }
    return acc;
  }, []);
  // first item in array is used as xaxis title.
  // if hideXaxisTitle=true, first item is removed from array
  if (hideXaxisTitle) {
    chartLegends.shift();
  }
    return chartLegends;

};

export const textCenterPlugin = () => ({
  id: 'textCenter',
  beforeDraw: function(chart) {
    const height = chart.chart.height;
    const ctx = chart.chart.ctx;
    ctx.restore();
    const fontSize = (height / 210).toFixed(2);
    ctx.font = `${fontSize}em sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#333';
    const chartArea = chart.chartArea;
    if (!chartArea) { return; }
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;
    const datasets = chart?.config?.data?.datasets || [];
    // Use the first value from the first dataset's total array
    const firstDataset = datasets?.[0] || {};
    const totalValue = Array.isArray(firstDataset.total) ? Number(firstDataset.total[0]) : 0;
    const displayText = Number.isFinite(totalValue)
      ? totalValue.toFixed(2)
      : '';
    const fontSizeNum = parseFloat(fontSize);
    const offset = height / 15; // visual spacing
    ctx.font = `bold ${fontSize}em sans-serif`;
    ctx.fillText(displayText, centerX, centerY - offset / 2);
    const unitFontEm = (fontSizeNum * 0.5).toFixed(2);
    ctx.font = `bold ${unitFontEm}em sans-serif`;
    ctx.fillText('kgCO₂eq', centerX, centerY + offset / 2);

    ctx.save();
  }
});

export const prepareChartData = (
  isCircularChart,
  chartDataJson,
  type,
  details,
  labels,
  label,
  chartData,
  columns
) => {
  if (!isCircularChart) {
    buildNonCircularChart({
      chartDataJson,
      type,
      details,
      labels,
      label,
      chartData,
      columns,
    });
  } else {
    buildCircularChart({
      chartDataJson,
      type,
      details,
      labels,
      chartData,
    });
  }
  return chartData;
};

export const getYMax = (rawNonCircularChartData, isErrorgraph) =>{
  let divider = 5;
  let yMax = 0;
  const yMaxObj = {}
  if (isErrorgraph){
    const ffr_plus_me_all = rawNonCircularChartData.ffr_plus_me.concat(rawNonCircularChartData.ffr_plus_me_100_pc);
    const filtered = ffr_plus_me_all.filter((el) => (String(el)!="null"));
    const maxY = Math.ceil(Math.max(...filtered));
    divider = maxY < divider ? 1 : divider;
    yMaxObj.isMultiple = maxY % divider === 0;
    yMax =  Math.ceil(maxY/ divider) * divider;
  }
  yMaxObj.yMax = yMax ;
  yMaxObj.divider = divider;
  return yMaxObj;
}